import { type ReactNode } from "react";
import { View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
    ReduceMotion,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";

import { windowShiftDeltaFromPan } from "./periodSwipe";

const DRAG_RESISTANCE = 0.55;
const DRAG_CLAMP_PX = 160;
const SNAP_MS = 180;

type PeriodSwipeAreaProps = {
    children: ReactNode;
    onShiftBy: (delta: number) => void;
};

export function PeriodSwipeArea({
    children,
    onShiftBy,
}: PeriodSwipeAreaProps) {
    const translateX = useSharedValue(0);

    const pan = Gesture.Pan()
        .activeOffsetX([-24, 24])
        .failOffsetY([-16, 16])
        .onUpdate((event) => {
            const dragged = event.translationX * DRAG_RESISTANCE;
            translateX.value = Math.max(
                -DRAG_CLAMP_PX,
                Math.min(DRAG_CLAMP_PX, dragged),
            );
        })
        .onEnd((event) => {
            const delta = windowShiftDeltaFromPan(
                event.translationX,
                event.velocityX,
            );

            if (delta !== 0) {
                runOnJS(onShiftBy)(delta);
            }

            translateX.value = withTiming(0, {
                duration: SNAP_MS,
                reduceMotion: ReduceMotion.System,
            });
        });

    const animatedStyle = useAnimatedStyle(() => ({
        opacity: 1 - Math.abs(translateX.value) / 480,
        transform: [{ translateX: translateX.value }],
    }));

    return (
        <GestureDetector gesture={pan}>
            <Animated.View
                collapsable={false}
                style={[{ flex: 1 }, animatedStyle]}
            >
                <View className="flex-1 gap-6">{children}</View>
            </Animated.View>
        </GestureDetector>
    );
}
